// Sub-component for IncidentEmailSettingsModal
// UPDATED: Validation now fires on blur only (not on change), empty-field errors only on submit attempt.
// Error messages repositioned below the input. Green success styling removed to reduce visual noise.
// UPDATED: Added aria-label to the role=switch toggle button for screen reader accessibility.

import { Plus, X, AlertCircle } from 'lucide-react';
import type { ReactNode } from 'react';

export interface EmailEntry {
  value: string;
  blurred: boolean;
}

interface CategoryEmailSectionProps {
  icon: ReactNode;
  label: string;
  badgeColor: string;
  isEnabled: boolean;
  emails: EmailEntry[];
  submittedOnce: boolean;
  onToggle: () => void;
  onEmailChange: (index: number, value: string) => void;
  onEmailBlur: (index: number) => void;
  onEmailRemove: (index: number) => void;
  onEmailAdd: () => void;
}

function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}

export function CategoryEmailSection({
  icon,
  label,
  badgeColor,
  isEnabled,
  emails,
  submittedOnce,
  onToggle,
  onEmailChange,
  onEmailBlur,
  onEmailRemove,
  onEmailAdd,
}: CategoryEmailSectionProps) {
  const hasEmptyEmail = emails.some(e => e.value.trim() === '');

  return (
    <div className="border border-gray-100 rounded-xl p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className={`p-2 rounded-lg ${badgeColor}`}>
            {icon}
          </span>
          <span className="font-semibold text-gray-900 text-sm">{label}</span>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={isEnabled}
          aria-label={`Activer les notifications pour ${label}`}
          onClick={onToggle}
          className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${
            isEnabled ? 'bg-primary-600' : 'bg-gray-200'
          }`}
        >
          <span
            className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow-sm transform transition-transform duration-200 ${
              isEnabled ? 'translate-x-5' : 'translate-x-0'
            }`}
          />
        </button>
      </div>

      {isEnabled && (
        <div className="space-y-2 pt-1">
          {emails.map((entry, index) => {
            const isInvalid = entry.blurred && entry.value.trim() !== '' && !isValidEmail(entry.value);
            const isEmpty = submittedOnce && entry.value.trim() === '';
            const hasError = isInvalid || isEmpty;

            return (
              <div key={index} className="space-y-1">
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <input
                      type="email"
                      value={entry.value}
                      onChange={e => onEmailChange(index, e.target.value)}
                      onBlur={() => onEmailBlur(index)}
                      placeholder="adresse@exemple.com"
                      className={`w-full px-3 py-2 text-sm border rounded-lg outline-none transition-colors ${
                        hasError
                          ? 'border-red-300 focus:ring-2 focus:ring-red-500 focus:border-red-500 pr-8'
                          : 'border-gray-200 focus:ring-2 focus:ring-primary-500 focus:border-primary-500'
                      }`}
                    />
                    {hasError && (
                      <AlertCircle size={14} className="absolute right-2 top-1/2 -translate-y-1/2 text-red-400 pointer-events-none" />
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => onEmailRemove(index)}
                    className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors shrink-0"
                    aria-label="Supprimer cette adresse"
                  >
                    <X size={14} />
                  </button>
                </div>
                {hasError && (
                  <p className="text-xs text-red-500 pl-1 animate-[fadeIn_0.15s_ease-in]">
                    {isEmpty ? 'Adresse requise' : 'Format invalide'}
                  </p>
                )}
              </div>
            );
          })}

          <button
            type="button"
            onClick={onEmailAdd}
            disabled={hasEmptyEmail}
            className="flex items-center gap-1.5 text-xs text-primary-600 hover:text-primary-700 font-medium disabled:text-gray-400 disabled:cursor-not-allowed transition-colors mt-1"
          >
            <Plus size={14} />
            Ajouter une adresse
          </button>
        </div>
      )}
    </div>
  );
}

export { isValidEmail };
